// Core packages
import { Component, Inject, OnInit } from "@angular/core";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

// Custom packages

/** A destination offered by the move dialog */
export interface DocumentTemplateMoveDialogOption {
  value: string;
  label: string;
  /** Secondary text (identifier) shown next to the label */
  hint?: string;
  disabled?: boolean;
}

/** A group of destinations. An empty label renders a flat list. */
export interface DocumentTemplateMoveDialogGroup {
  label: string;
  options: DocumentTemplateMoveDialogOption[];
}

/** Data the move dialog needs */
export interface DocumentTemplateMoveDialogData {
  title: string;
  /** Node being moved */
  subject: string;
  /** Where the node currently lives */
  currentLabel: string;
  selectLabel: string;
  /** Consequence the user must acknowledge before confirming */
  warning: string;
  groups: DocumentTemplateMoveDialogGroup[];
}

/**
 * Destination picker of a move, shared by the section move (level 2 -> another area)
 * and the document move (level 3 -> another section).
 *
 * The dialog IS the confirmation step: the consequence of the move is stated inside
 * it and the primary button confirms, so the user never picks a destination in one
 * place and confirms something else in another (the legacy page keeps a page level
 * form whose value survives a change of selection).
 *
 * Closes with the chosen destination id, or with undefined when cancelled.
 *
 * @since 3.0.0
 */
@Component({
  selector: "app-document-template-move-dialog",
  templateUrl: "./document-template-move-dialog.component.html",
  styleUrls: ["./document-template-move-dialog.component.scss"],
})
export class DocumentTemplateMoveDialogComponent implements OnInit {
  selectedValue = "";
  search = "";
  visibleGroups: DocumentTemplateMoveDialogGroup[] = [];

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<DocumentTemplateMoveDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentTemplateMoveDialogData
  ) {}

  /**
   * Init component
   *
   * @since 3.0.0
   */
  ngOnInit(): void {
    this.applySearch();
  }

  /**
   * Filter the destinations by label or identifier, dropping the groups left empty.
   *
   * @since 3.0.0
   */
  applySearch(): void {
    const groups =
      this.data && Array.isArray(this.data.groups) ? this.data.groups : [];
    const query = (this.search || "").trim().toLowerCase();

    if (!query) {
      this.visibleGroups = groups.filter(
        (group) => group.options && group.options.length > 0
      );
      return;
    }

    const filtered: DocumentTemplateMoveDialogGroup[] = [];
    groups.forEach((group) => {
      const options = (group.options || []).filter(
        (option) =>
          (option.label || "").toLowerCase().indexOf(query) >= 0 ||
          (option.hint || "").toLowerCase().indexOf(query) >= 0
      );
      if (options.length) {
        filtered.push({ label: group.label, options });
      }
    });
    this.visibleGroups = filtered;
  }

  /**
   * True when there is no destination at all to pick from.
   *
   * @since 3.0.0
   */
  get hasDestinations(): boolean {
    const groups =
      this.data && Array.isArray(this.data.groups) ? this.data.groups : [];
    return groups.some((group) => group.options && group.options.length > 0);
  }

  /**
   * Confirm the move.
   *
   * @since 3.0.0
   */
  onConfirm(): void {
    if (!this.selectedValue) {
      return;
    }
    this.dialogRef.close(this.selectedValue);
  }

  /**
   * Dismiss without moving anything.
   *
   * @since 3.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }
}
